import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, MenuItem,
  IconButton, Collapse, Chip, Button, Pagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { sendServerRequest } from '../../../server/request';
import { openGlobalSnack } from '../../utils/snack';
import { CircularLoading } from '../../utils/loading';

const ENDPOINT = '/server/fgtsCommand/consults';
const PAGE_SIZE = 20;

type ConsultLog = {
  id: string;
  cpf: string;
  provider: string;
  v8_login: string | null;
  source: string | null;
  channel: string | null;
  client_id: number | null;
  chat_id: number | null;
  result: string;
  v8_status: string | null;
  balance_id: string | null;
  amount: number | null;
  error_message: string | null;
  http_status: number | null;
  body: string | null;
  response: string | null;
  resolved_by: string | null;
  duration_ms: number | null;
  createdAt: string;
};

type ChipColor = 'success' | 'warning' | 'error' | 'info' | 'default' | 'secondary' | 'primary';

type Summary = { results: Record<string, number>; channels: Record<string, number> };

type Filters = { provider: string; result: string; channel: string; cpf: string; from: string; to: string };

const EMPTY_FILTERS: Filters = { provider: '', result: '', channel: '', cpf: '', from: '', to: '' };

/** Canal de onde veio a consulta (fgts_consult_log.channel). 'none' = sem canal. */
const CHANNEL_LABELS: Record<string, { label: string; color: ChipColor }> = {
  whatsapp: { label: 'WhatsApp', color: 'success' },
  evolution: { label: 'Evolution', color: 'success' },
  instagram: { label: 'Instagram', color: 'secondary' },
  telegram: { label: 'Telegram', color: 'info' },
  autocontratacao: { label: 'Página auto-contratação', color: 'primary' },
  app: { label: 'Aplicativo', color: 'primary' },
  none: { label: 'Sem canal', color: 'default' },
};

/** Gatilho da consulta (fgts_consult_log.source). */
const SOURCE_LABELS: Record<string, string> = {
  bot: 'Bot',
  bot_proposal: 'Bot (proposta)',
  birth_cron: 'Cron aniversário',
  internal: 'API interna',
  site_pending: 'Acompanhamento pendente',
  live_test: 'Teste',
  webhook_test: 'Teste',
};

const RESULT_LABELS: Record<string, { label: string; color: ChipColor }> = {
  ok: { label: 'Com saldo', color: 'success' },
  pending: { label: 'Em andamento', color: 'info' },
  noFounds: { label: 'Sem saldo', color: 'default' },
  needAuthBanks: { label: 'Banco não autorizado', color: 'warning' },
  needBeAnnibersaryMembership: { label: 'Sem adesão', color: 'warning' },
  waitBirth: { label: 'Aguardar aniversário', color: 'default' },
  needGoToFGTSCaixa: { label: 'Procurar Caixa', color: 'default' },
  offNow: { label: 'Provider fora', color: 'error' },
  internalError: { label: 'Erro interno V8', color: 'error' },
  exceededRequests: { label: 'Limite de requisições', color: 'error' },
  unknown: { label: 'Desconhecido', color: 'error' },
  error: { label: 'Erro', color: 'error' },
};

const RESOLVED_LABELS: Record<string, string> = {
  webhook: 'Webhook',
  webhook_late: 'Webhook (atrasado)',
  polling: 'GET (sem webhook)',
  recovery: 'Proteção 5 min',
  post_error: 'Erro no POST',
  waiting: 'Aguardando',
  timeout: 'Sem resposta',
};

function resultChip(result: string) {
  return RESULT_LABELS[result] ?? { label: result, color: 'default' as const };
}

function channelChip(channel: string | null) {
  const key = channel ?? 'none';
  return CHANNEL_LABELS[key] ?? { label: key, color: 'default' as const };
}

function fmtDate(iso: string) {
  try { return new Date(iso).toLocaleString('pt-BR'); } catch { return iso; }
}

function fmtMoney(v: number | null) {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function prettyJson(raw: string | null) {
  if (!raw) return '';
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
}

const preStyle: React.CSSProperties = {
  fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
  maxHeight: 320, overflow: 'auto', margin: '4px 0 8px', background: '#0000000a', padding: 8, borderRadius: 4,
};

export function FgtsConsultsPanel() {
  const [consults, setConsults] = useState<ConsultLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [summary, setSummary] = useState<Summary>({ results: {}, channels: {} });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilter = (key: keyof Filters, value: string) => setFilters((f) => ({ ...f, [key]: value }));

  // Os chips aplicam o filtro na hora: recebem os filtros novos em vez de esperar o setState.
  function applyFilter(key: 'result' | 'channel', value: string) {
    const next = { ...filters, [key]: filters[key] === value ? '' : value };
    setFilters(next);
    load(1, next);
  }

  function load(toPage: number, f: Filters = filters) {
    setLoading(true);
    const q = new URLSearchParams({ page: String(toPage), pageSize: String(PAGE_SIZE) });
    if (f.provider) q.set('provider', f.provider);
    if (f.result) q.set('result', f.result);
    if (f.channel) q.set('channel', f.channel);
    if (f.cpf.trim()) q.set('cpf', f.cpf.trim());
    if (f.from) q.set('from', f.from);
    if (f.to) q.set('to', f.to);
    sendServerRequest('GET', `${ENDPOINT}/list?${q.toString()}`)
      .then((e) => {
        const data = e.data?.data ?? {};
        setConsults(data.consults ?? []);
        setTotal(data.total ?? 0);
        setSummary(data.summary ?? { results: {}, channels: {} });
        setPage(toPage);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        openGlobalSnack('Não foi possível carregar as consultas.', 'error');
      });
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <h3 style={{ marginBottom: 4, textAlign: 'center' }}>Consultas de saldo FGTS</h3>
      <p style={{ fontSize: 12, color: '#000000a0', marginTop: 0, marginBottom: 16, textAlign: 'center' }}>
        Todas as consultas, uma linha por banco tentado, com o retorno completo da V8.
        "Em andamento" é fechada pelo webhook ou pela proteção de 5 min. Canal: de onde veio a consulta
        (WhatsApp, Evolution, Instagram, Telegram, página de auto-contratação ou aplicativo). Apagadas após 30 dias.
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <TextField select size="small" label="Canal" value={filters.channel} style={{ minWidth: 200 }}
          onChange={(e) => setFilter('channel', e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          {Object.entries(CHANNEL_LABELS).map(([k, v]) => (
            <MenuItem key={k} value={k}>{v.label}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Banco" value={filters.provider} style={{ minWidth: 140 }}
          onChange={(e) => setFilter('provider', e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="girotech">Girotech</MenuItem>
          <MenuItem value="bms">BMS</MenuItem>
        </TextField>
        <TextField select size="small" label="Resultado" value={filters.result} style={{ minWidth: 200 }}
          onChange={(e) => setFilter('result', e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          {Object.entries(RESULT_LABELS).map(([k, v]) => (
            <MenuItem key={k} value={k}>{v.label}</MenuItem>
          ))}
        </TextField>
        <TextField size="small" label="CPF" value={filters.cpf} onChange={(e) => setFilter('cpf', e.target.value)} />
        <TextField size="small" type="date" label="De" value={filters.from} InputLabelProps={{ shrink: true }}
          onChange={(e) => setFilter('from', e.target.value)} />
        <TextField size="small" type="date" label="Até" value={filters.to} InputLabelProps={{ shrink: true }}
          onChange={(e) => setFilter('to', e.target.value)} />
        <Button variant="contained" onClick={() => load(1)}>Filtrar</Button>
        <Button onClick={() => { setFilters(EMPTY_FILTERS); load(1, EMPTY_FILTERS); }}>Limpar</Button>
        <IconButton onClick={() => load(page)}><RefreshIcon /></IconButton>
      </div>

      <SummaryChips title="Status" counts={summary.results} active={filters.result}
        label={(k) => resultChip(k)} onClick={(k) => applyFilter('result', k)} />
      <SummaryChips title="Canal" counts={summary.channels} active={filters.channel}
        label={(k) => channelChip(k === 'none' ? null : k)} onClick={(k) => applyFilter('channel', k)} />

      {loading ? <CircularLoading /> : (
        <Paper variant="outlined" style={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Data</TableCell>
                <TableCell>Banco</TableCell>
                <TableCell>CPF</TableCell>
                <TableCell>Resultado</TableCell>
                <TableCell>Saldo</TableCell>
                <TableCell>Canal</TableCell>
                <TableCell>Mensagem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {consults.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center" style={{ color: '#00000080' }}>Nenhuma consulta.</TableCell></TableRow>
              )}
              {consults.map((c) => (
                <React.Fragment key={c.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => setExpanded(expanded === c.id ? null : c.id)}>
                        {expanded === c.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDate(c.createdAt)}</TableCell>
                    <TableCell>{c.provider}</TableCell>
                    <TableCell>{c.cpf}</TableCell>
                    <TableCell>
                      <Chip size="small" label={resultChip(c.result).label}
                        color={resultChip(c.result).color} variant="outlined" />
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtMoney(c.amount)}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      <Chip size="small" label={channelChip(c.channel).label} color={channelChip(c.channel).color} />
                      <div style={{ fontSize: 11, color: '#00000080' }}>
                        {c.source ? (SOURCE_LABELS[c.source] ?? c.source) : '—'}
                      </div>
                    </TableCell>
                    <TableCell style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.error_message ?? '—'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} style={{ paddingTop: 0, paddingBottom: 0, border: 0 }}>
                      <Collapse in={expanded === c.id} unmountOnExit>
                        <div style={{ padding: 12, background: '#00000008', fontSize: 12 }}>
                          <div style={{ marginBottom: 8 }}>
                            <b>Resolvida por:</b> {c.resolved_by ? (RESOLVED_LABELS[c.resolved_by] ?? c.resolved_by) : '—'} &nbsp;|&nbsp;
                            <b>Duração:</b> {c.duration_ms !== null ? `${(c.duration_ms / 1000).toFixed(1)}s` : '—'} &nbsp;|&nbsp;
                            <b>Status V8:</b> {c.v8_status ?? '—'} &nbsp;|&nbsp;
                            <b>HTTP:</b> {c.http_status ?? '—'}
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <b>Conta V8:</b> {c.v8_login ?? '—'} &nbsp;|&nbsp;
                            <b>balanceId:</b> {c.balance_id ?? '—'} &nbsp;|&nbsp;
                            <b>client_id:</b> {c.client_id ?? '—'} &nbsp;|&nbsp;
                            <b>chat_id:</b> {c.chat_id ?? '—'}
                          </div>
                          {c.error_message && <div style={{ marginBottom: 8 }}><b>Mensagem da V8:</b> {c.error_message}</div>}
                          {c.body && (
                            <>
                              <b>Body enviado:</b>
                              <pre style={preStyle}>{prettyJson(c.body)}</pre>
                            </>
                          )}
                          <b>Retorno completo (POST, webhook, GET):</b>
                          <pre style={preStyle}>{prettyJson(c.response) || '—'}</pre>
                        </div>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
          <Pagination count={pages} page={page} onChange={(_, p) => load(p)} />
        </div>
      )}
    </div>
  );
}

/** Contagem por status/canal dos filtros atuais; clicar filtra (de novo, limpa). */
function SummaryChips(props: {
  title: string;
  counts: Record<string, number>;
  active: string;
  label: (key: string) => { label: string; color: ChipColor };
  onClick: (key: string) => void;
}) {
  const entries = Object.entries(props.counts).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
      <span style={{ fontSize: 12, color: '#000000a0', minWidth: 48 }}>{props.title}:</span>
      {entries.map(([k, n]) => {
        const l = props.label(k);
        return (
          <Chip key={k} size="small" clickable color={l.color}
            variant={props.active === k ? 'filled' : 'outlined'}
            label={`${l.label}: ${n}`} onClick={() => props.onClick(k)} />
        );
      })}
    </div>
  );
}
